import { Component, inject } from '@angular/core';
import { commonImports } from '../shared/table-interface';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CodeableConceptField, FormFields, generalFieldsData, GroupField, IndividualField, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { firstValueFrom, forkJoin, map, catchError, of, switchMap, combineLatest } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { UtilityService } from '../shared/utility.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorService } from '../shared/error.service';
import { formMetaData } from '../shared/dynamic-forms.interface2';
import { MatSelectModule } from '@angular/material/select';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { Bundle, ChargeItem, ChargeItemDefinition, Invoice, PaymentNotice, PaymentReconciliation } from 'fhir/r4';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ChipsDirective } from '../chips.directive';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { ReferenceDisplayService } from '../shared/reference-display.service';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ChargeItemPricingService } from './charge-item-pricing.service';
import { AuthService } from '../shared/auth/auth.service';
import { DisableCache } from '../loading.interceptor';
import { InvoicePrintService } from '../shared/invoice-print.service';




@Component({
  selector: 'app-encounter-v2',
  imports: [
    ...commonImports,
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatTableModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
    EmptyStateComponent,
    ChipsDirective,
    ReferenceDisplayDirective
  ],
  templateUrl: './encounter-v2.component.html',
  styleUrl: './encounter-v2.component.scss'
})
export class EncounterV2Component {
  medicine1UnitPrice = 5000;
  quantity1 = 1
  utilityService = inject(UtilityService);
  invOrClaim: string = "Invoice"
  referenceDisplayService = inject(ReferenceDisplayService);

  increaseQ() {
    this.quantity1++;
  }
  decreaseQ() {
    if (this.quantity1 > 1) {
      this.quantity1--;
    }
  }

  chargesFormFields?: Map<string, {
    [key: string]: any,
    formFields: FormFields[]
  }>
  formFieldService = inject(FormFieldsSelectDataService);
  patientId = 'b47fc122-f4d9-4970-81c7-97badc18e311';
  encounterId = 'aa8209c4-1a60-4dff-8924-b724ade644a8'
  http = inject(HttpClient);
  auth = inject(AuthService);
  backendApiEndPoint = inject(backendEndPointToken)

  canManageBilling(): boolean {
    const activeRole = this.auth.user.getValue()?.role?.toLowerCase() || '';
    return activeRole === 'cashier';
  }
  invoicesTypeMap: Map<string, (Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>)[]> = new Map();
  tableDataSourceMap = new Map<string, MatTableDataSource<Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>>>();

  medicationInvoices: (Invoice & { relatedChargeItem: ChargeItem })[] = [];

  // Material table view-model
  medicationInvoiceRows = new MatTableDataSource<Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>>([]);
  labInvoiceRows = new MatTableDataSource<Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>>([]);
  otherServiceInvoiceRows = new MatTableDataSource<Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>>([]);

  medicationInvoiceDisplayedColumns = ['date', 'status', 'medication', 'price', 'quantity', 'totalGross', 'menu'];
  labInvoiceDisplayedColumns = ['date', 'status', 'service', 'price', 'quantity', 'totalGross', 'menu'];
  otherServiceInvoiceDisplayedColumns = ['date', 'status', 'service', 'price', 'quantity', 'totalGross', 'menu'];

  pricingService = inject(ChargeItemPricingService);
  invoicePrintService = inject(InvoicePrintService);

  private extractInvoiceId(row: any): string | null {
    const direct = (row as any)?.id ? String((row as any).id) : '';
    if (direct) return direct;
    const display = String((row as any)?.invoiceIdDisplay ?? '').trim();
    if (display.startsWith('Invoice/')) return display.split('/').pop() || null;
    return display || null;
  }

  private isInvoicePaid(row: any): boolean {
    const status = String((row as any)?.status ?? (row as any)?.invoiceStatusDisplay ?? '').toLowerCase();
    return status === 'balanced';
  }

  private extractReferenceParts(ref?: string | null): { resourceType: string; id: string } | null {
    const raw = (ref ?? '').toString().trim();
    if (!raw) return null;

    // Strip any base URL prefix (e.g., https://server/fhir/Observation/123)
    const m = raw.match(/([A-Za-z]+)\/([^\/]+)$/);
    if (!m) return null;
    return { resourceType: m[1], id: m[2] };
  }

  private extractServiceReferencesFromChargeItem(ci: ChargeItem): string[] {
    const refs: string[] = [];
    const service = (ci as any)?.service;
    if (Array.isArray(service)) {
      for (const s of service) {
        const r = (s?.reference ?? '').toString().trim();
        if (r) refs.push(r);
      }
    }
    return refs;
  }

  private canReverseForServiceReference(ref: string) {
    const parts = this.extractReferenceParts(ref);
    if (!parts) {
      return of({ reference: ref, allowed: false, reason: `Cannot reverse payment: unrecognized service reference (${ref}).` });
    }

    const rt = parts.resourceType;
    const id = parts.id;

    // Only enforce for the resource types you asked for.
    const lower = rt.toLowerCase();
    const supported = lower === 'medicationdispense' || lower === 'observation' || lower === 'diagnosticreport';
    if (!supported) {
      return of({ reference: ref, allowed: true });
    }

    const url = `${this.backendApiEndPoint}/${rt}/${id}`;
    return this.http.get<any>(url, { headers: { 'Accept': 'application/fhir+json' } }).pipe(
      map((res: any) => {
        const status = (res?.status ?? '').toString().toLowerCase();

        if (lower === 'medicationdispense') {
          // MedicationDispense: block reversal if already completed.
          if (status === 'completed') {
            return { reference: ref, allowed: false, reason: 'Cannot reverse payment: medication has already been dispensed (MedicationDispense is completed).' };
          }
          return { reference: ref, allowed: true };
        }

        if (lower === 'observation') {
          // Observation: block reversal if final or beyond.
          const finalish = new Set(['final', 'amended', 'corrected', 'appended']);
          if (finalish.has(status)) {
            return { reference: ref, allowed: false, reason: 'Cannot reverse payment: lab result has already been finalized (Observation is final).' };
          }
          return { reference: ref, allowed: true };
        }

        if (lower === 'diagnosticreport') {
          const finalish = new Set(['final', 'amended', 'corrected', 'appended']);
          if (finalish.has(status)) {
            return { reference: ref, allowed: false, reason: 'Cannot reverse payment: diagnostic report has already been finalized (DiagnosticReport is final).' };
          }
          return { reference: ref, allowed: true };
        }

        return { reference: ref, allowed: true };
      }),
      catchError((err) => {
        console.error('Failed to verify service status for reversal', { ref, err });
        return of({ reference: ref, allowed: false, reason: `Cannot reverse payment: failed to verify status for ${rt}/${id}.` });
      })
    );
  }

  private findPaymentResourcesForInvoice(invoiceId: string) {
    const invoiceRef = `Invoice/${invoiceId}`;

    const pickMostRelevantPR = (prs: PaymentReconciliation[]): PaymentReconciliation | null => {
      const filtered = (prs || []).filter(pr => {
        const details = (pr as any)?.detail ?? [];
        return Array.isArray(details) && details.some((d: any) => d?.request?.reference === invoiceRef);
      });
      const active = filtered.find(pr => (pr?.status ?? '').toString().toLowerCase() === 'active');
      return active ?? filtered[0] ?? null;
    };

    const pickMostRelevantPN = (pns: PaymentNotice[]): PaymentNotice | null => {
      const active = (pns || []).find(pn => (pn?.status ?? '').toString().toLowerCase() === 'active');
      return active ?? (pns[0] ?? null);
    };

    const findPR$ = this.http.get<Bundle<PaymentReconciliation>>(
      `${this.backendApiEndPoint}/PaymentReconciliation?request=${encodeURIComponent(invoiceRef)}&_count=50`,
      { headers: { 'Accept': 'application/fhir+json' } }
    ).pipe(
      map((bundle) => (bundle.entry ?? []).map(e => e.resource as PaymentReconciliation).filter(Boolean)),
      map((prs) => pickMostRelevantPR(prs)),
      catchError((err) => {
        console.warn('PaymentReconciliation search by request failed; falling back to client-side filtering.', err);
        return this.http.get<Bundle<PaymentReconciliation>>(
          `${this.backendApiEndPoint}/PaymentReconciliation?_count=200`,
          { headers: { 'Accept': 'application/fhir+json' } }
        ).pipe(
          map((bundle) => (bundle.entry ?? []).map(e => e.resource as PaymentReconciliation).filter(Boolean)),
          map((prs) => pickMostRelevantPR(prs)),
          catchError(() => of(null))
        );
      })
    );

    return findPR$.pipe(
      switchMap((pr) => {
        if (!pr?.id) {
          return of({ paymentReconciliation: null as PaymentReconciliation | null, paymentNotice: null as PaymentNotice | null });
        }

        const prRef = `PaymentReconciliation/${pr.id}`;
        const findPN$ = this.http.get<Bundle<PaymentNotice>>(
          `${this.backendApiEndPoint}/PaymentNotice?payment=${encodeURIComponent(prRef)}&_count=50`,
          { headers: { 'Accept': 'application/fhir+json' } }
        ).pipe(
          map((bundle) => (bundle.entry ?? []).map(e => e.resource as PaymentNotice).filter(Boolean)),
          map((pns) => pickMostRelevantPN(pns)),
          catchError((err) => {
            console.warn('PaymentNotice search by payment failed; falling back to client-side filtering.', err);
            return this.http.get<Bundle<PaymentNotice>>(
              `${this.backendApiEndPoint}/PaymentNotice?_count=200`,
              { headers: { 'Accept': 'application/fhir+json' } }
            ).pipe(
              map((bundle) => (bundle.entry ?? []).map(e => e.resource as PaymentNotice).filter(Boolean)),
              map((pns) => pickMostRelevantPN((pns || []).filter(pn => (pn as any)?.payment?.reference === prRef))),
              catchError(() => of(null))
            );
          })
        );

        return findPN$.pipe(map((pn) => ({ paymentReconciliation: pr, paymentNotice: pn })));
      })
    );
  }

  reversePayment(row: any): void {
    const invoiceId = this.extractInvoiceId(row);
    if (!invoiceId) {
      this.errorService.openandCloseError('Invoice id is missing.');
      return;
    }

    // Only allow reversal for invoices that are currently paid/balanced.
    if (!this.isInvoicePaid(row)) {
      this.errorService.openandCloseError('Only PAID invoices can be reversed.');
      return;
    }

    const patchUi = (nextStatus: string) => {
      const patchDs = (ds: MatTableDataSource<any>) => {
        const current = ds.data || [];
        ds.data = current.map(r => {
          if ((r as any)?.id !== invoiceId) return r;
          return { ...r, status: nextStatus, invoiceStatusDisplay: nextStatus };
        });
      };

      for (const ds of this.tableDataSourceMap.values()) patchDs(ds);
      patchDs(this.medicationInvoiceRows);
      patchDs(this.labInvoiceRows);
      patchDs(this.otherServiceInvoiceRows);
    };

    const markUnpaidStatusOnly = (invoiceSnapshot?: Invoice | null) => {
      const source$ = invoiceSnapshot
        ? of(invoiceSnapshot)
        : this.http.get<Invoice>(`${this.backendApiEndPoint}/Invoice/${invoiceId}`, {
          headers: { 'Accept': 'application/fhir+json' }
        });

      return source$.pipe(
        switchMap((inv) => {
          if (!inv) {
            throw new Error('Invoice not found');
          }
          const updatedInvoice: Invoice = { ...inv, status: 'issued' };
          return this.http.put(
            `${this.backendApiEndPoint}/Invoice/${invoiceId}`,
            updatedInvoice,
            {
              headers: {
                'Content-Type': 'application/fhir+json',
                'Accept': 'application/fhir+json',
                'Prefer': 'return=representation'
              }
            }
          );
        })
      );
    };

    // 1) Fetch the invoice
    // 2) Fetch its ChargeItems
    // 3) Verify referenced services are not completed/final
    // 4) Find PaymentReconciliation + PaymentNotice
    // 5) Transaction-update all three resources
    this.http.get<Invoice>(`${this.backendApiEndPoint}/Invoice/${invoiceId}`, { headers: { 'Accept': 'application/fhir+json' } }).pipe(
      switchMap((inv) => {
        const lineItems = (inv as any)?.lineItem ?? [];
        const chargeItemIds = (lineItems as any[])
          .map(li => this.extractChargeItemIdFromRef(li?.chargeItemReference?.reference))
          .filter(Boolean) as string[];

        const uniqueIds = [...new Set(chargeItemIds.map(String))];
        if (!uniqueIds.length) {
          // No charge items linked; allow reversal to proceed (status + payment resources only)
          return of({ inv, chargeItems: [] as ChargeItem[] });
        }

        const gets = uniqueIds.map((id) =>
          this.http.get<ChargeItem>(`${this.backendApiEndPoint}/ChargeItem/${id}`, { headers: { 'Accept': 'application/fhir+json' } })
            .pipe(catchError(() => of(null as any)))
        );

        return forkJoin(gets).pipe(
          map((cis) => ({ inv, chargeItems: (cis || []).filter(Boolean) as ChargeItem[] }))
        );
      }),
      switchMap(({ inv, chargeItems }) => {
        const serviceRefs = [...new Set(chargeItems.flatMap(ci => this.extractServiceReferencesFromChargeItem(ci)))];
        if (!serviceRefs.length) {
          return of({ inv, serviceChecks: [] as any[] });
        }
        return forkJoin(serviceRefs.map(ref => this.canReverseForServiceReference(ref))).pipe(
          map((checks) => ({ inv, serviceChecks: checks }))
        );
      }),
      switchMap(({ inv, serviceChecks }) => {
        const blocked = (serviceChecks || []).find((c: any) => c?.allowed === false);
        if (blocked) {
          this.errorService.openandCloseError(blocked?.reason || 'Cannot reverse payment due to service status.');
          return of(null);
        }
        return this.findPaymentResourcesForInvoice(invoiceId).pipe(map((pay) => ({ inv, pay })));
      }),
      switchMap((payload) => {
        if (!payload) return of(null);
        const { inv, pay } = payload as any;

        const updatedInvoice: Invoice = { ...(inv as any), status: 'issued' };

        // If the invoice was marked paid without payment resources (status-only), just revert invoice status.
        if (!pay?.paymentReconciliation?.id) {
          return markUnpaidStatusOnly(updatedInvoice).pipe(map((updated: any) => ({ updated })));
        }

        const updatedPaymentReconciliation: PaymentReconciliation = {
          ...(pay.paymentReconciliation as any),
          status: 'entered-in-error'
        };

        const entries: any[] = [
          { request: { method: 'PUT', url: `Invoice/${invoiceId}` }, resource: updatedInvoice },
          { request: { method: 'PUT', url: `PaymentReconciliation/${pay.paymentReconciliation.id}` }, resource: updatedPaymentReconciliation }
        ];

        if (pay?.paymentNotice?.id) {
          const updatedPaymentNotice: PaymentNotice = {
            ...(pay.paymentNotice as any),
            status: 'entered-in-error'
          };
          entries.push({ request: { method: 'PUT', url: `PaymentNotice/${pay.paymentNotice.id}` }, resource: updatedPaymentNotice });
        }

        const bundle: Bundle = {
          resourceType: 'Bundle',
          type: 'transaction',
          entry: entries
        };

        return this.http.post<any>(`${this.backendApiEndPoint}`, bundle, {
          headers: {
            'Content-Type': 'application/fhir+json',
            'Accept': 'application/fhir+json',
            'Prefer': 'return=representation'
          }
        }).pipe(map((txnResponse: any) => ({ txnResponse })));
      }),
      catchError((err) => {
        console.error('Failed to reverse payment', err);
        this.errorService.openandCloseError('Failed to reverse payment.');
        return of(null);
      })
    ).subscribe((result: any) => {
      if (!result) return;
      patchUi('issued');
    });
  }

  getSectionTotals(ds: MatTableDataSource<any> | null | undefined): { totalGrossDisplay: string; paidDisplay: string } {
    const rows = (ds?.data ?? []) as any[];
    const seenInvoiceIds = new Set<string>();

    const currencySet = new Set<string>();
    const addCurrencyFromRow = (row: any) => {
      const c = (
        (row as any)?.totalGross?.currency ??
        (row as any)?.totalNet?.currency ??
        (row as any)?.currency ??
        null
      );
      const code = (c ?? '').toString().trim();
      if (!code) {
        currencySet.add('MISSING');
      } else {
        currencySet.add(code.toUpperCase());
      }
    };

    let totalGross: number | string = '';
    let totalPaid: number = 0;

    for (const row of rows) {
      const id = this.extractInvoiceId(row);
      if (!id || seenInvoiceIds.has(id)) continue;
      seenInvoiceIds.add(id);

      addCurrencyFromRow(row);
      if ((row as any)?.totalGross) {
        const gross = this.moneyToNumber((row as any)?.totalGross);
        if (!totalGross) totalGross = 0 as number;
        totalGross = Number(totalGross) + gross;
        if (!totalPaid) totalPaid = 0 as number;
        if (this.isInvoicePaid(row)) totalPaid = Number(totalPaid) + gross;
      }
    }

    // Prevent displaying mathematically wrong totals when currencies differ (or are missing).
    if (currencySet.size !== 1) {
      return { totalGrossDisplay: 'Mixed currencies', paidDisplay: 'Mixed currencies' };
    }

    const currency = Array.from(currencySet)[0] === 'MISSING' ? 'NGN' : Array.from(currencySet)[0];
    if (totalGross !== '' && !Number.isNaN(totalGross)) {
// $#$      // /* alert(totalGross); */
      return {

        totalGrossDisplay: this.fmtMoney({ value: totalGross, currency }),
        paidDisplay: this.fmtMoney({ value: totalPaid, currency }),
      };

    } else {
      return {
        totalGrossDisplay: 'N/A',
        paidDisplay: this.fmtMoney({ value: totalPaid, currency }),
      }
    }

  }

  get overallTotalDisplay(): string {
    const allRows: any[] = [];
    for (const ds of this.tableDataSourceMap.values()) {
      allRows.push(...(ds.data ?? []));
    }
    return this.getSectionTotals({ data: allRows } as any).totalGrossDisplay;
  }

  get overallPaidDisplay(): string {
    const allRows: any[] = [];
    for (const ds of this.tableDataSourceMap.values()) {
      allRows.push(...(ds.data ?? []));
    }
    return this.getSectionTotals({ data: allRows } as any).paidDisplay;
  }

  private escapeHtml(s: any): string {
    return String(s ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }



  printInvoiceOrClaim(): void {
    const docType = (this.invOrClaim || 'Invoice').toString().toLowerCase().includes('claim')
      ? 'Insurance Claim'
      : 'Invoice';

    this.invoicePrintService.printEncounterInvoice(
        this.encounterId,
        this.patientId,
        docType,
        this.tableDataSourceMap,
        this.overallTotalDisplay,
        this.overallPaidDisplay
    );
  }

  private moneyToNumber(m: any): number {
    const n = Number(m?.value ?? m);
    return Number.isFinite(n) ? n : 0;
  }

  fmtMoney(m: any): string {
    const value = this.moneyToNumber(m);
    const currency = (m?.currency ?? 'NGN').toString() || 'NGN';
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(value);
  }

  hasManualPriceOverride(row: any): boolean {
    const chargeItem = row?.relatedChargeItem as ChargeItem | undefined;
    if (!chargeItem) return false;

    const overrideValue = Number((chargeItem as any)?.priceOverride?.value);
    if (!Number.isFinite(overrideValue) || overrideValue <= 0) {
      return false;
    }

    const overrideReason = String((chargeItem as any)?.overrideReason ?? '').trim();
    if (overrideReason === 'No ChargeItemDefinition available; manual price applied.') {
      return false;
    }

    const partOf = Array.isArray((chargeItem as any)?.partOf) ? (chargeItem as any).partOf : [];
    const referencesOriginalCharge = partOf.some((ref: any) => {
      const refStr = (ref?.reference ?? '').toString();
      return refStr.startsWith('ChargeItem/');
    });

    return !referencesOriginalCharge;
  }

  private coerceCurrencyCode(input: any): string {
    const c = (input?.code ?? input?.currency ?? input ?? '').toString().trim();
    return c || 'NGN';
  }

  private parseNumber(input: any): number | null {
    const n = Number(input);
    return Number.isFinite(n) ? n : null;
  }

  private openPriceDialog(prevVal: number | null = null, shouldIncludeOverrideReason: boolean = false) {
    if (!this.chargesFormFields) {
      this.errorService.openandCloseError('Price form is not ready yet. Please wait a moment and try again.');
      return null;
    }

    const formFields = this.utilityService
      .convertFormFields(this.chargesFormFields)
      .filter((field) => {
        const api = field?.generalProperties?.fieldApiName;
        return api === 'currency' || api === 'price';
      })
      .map((field) => {
        if (field?.generalProperties?.fieldApiName === 'price' && prevVal != null) {
          field.generalProperties.value = prevVal;
        }
        return field;
      });

    if (shouldIncludeOverrideReason) {
      formFields.push(<IndividualField>{
        generalProperties: {
          fieldApiName: 'overrideReason',
          fieldName: 'Override Reason',
          fieldLabel: 'Override Reason',
          fieldType: 'IndividualField',
          fieldHint: 'Provide a reason for overriding the default price.',
          isArray: false,
          isGroup: false,
          inputType: 'text',

          value: ''
        }
      });
    }

    return this.dialog.open(DynamicFormsV2Component, {
      width: '650px',
      data: {
        formMetaData: {
          formName: prevVal ? 'Update Price' : 'Add Price',
          formDescription: 'Set a price for this billed item.',
          submitText: prevVal ? 'Update Price' : 'Add Price',
          showSubmitButton: true,
          closeDialogOnSubmit: true,
        } as formMetaData,
        formFields
      }
    });
  }

  addPriceForRow(row: any, shouldIncludeOverrideReason: boolean = false): void {
    const invStatus = (row?.invoiceStatusDisplay ?? row?.status ?? '').toString().toLowerCase();
    if (invStatus === 'balanced') {
      this.errorService.openandCloseError('Cannot override price for a PAID invoice (status is balanced).');
      return;
    }

    const invoiceId = this.extractInvoiceId(row);
    const chargeItemId = (row?.relatedChargeItem as ChargeItem | undefined)?.id ?? null;
    if (!invoiceId || !chargeItemId) {
      this.errorService.openandCloseError('Missing Invoice or ChargeItem id for this row.');
      return;
    }

    const prev = this.parseNumber(row?.unitPriceValue) ?? this.parseNumber(row?.['unit price']) ?? null;
    const dlg = this.openPriceDialog(prev, shouldIncludeOverrideReason);
    if (!dlg) return;

    dlg.afterClosed().subscribe((result: any) => {
      result = result.values;
      if (!result) return;


      const unitPrice = this.parseNumber(result?.price ?? result?.Price ?? result?.amount);
      if (unitPrice == null || unitPrice <= 0) {
        this.errorService.openandCloseError('Please provide a valid price.');
        return;
      }

      const currency = this.coerceCurrencyCode(result?.currency) || (row?.currency || 'NGN');

      this.pricingService
        .applyManualPriceToChargeItemAndInvoice({
          chargeItemId: String(chargeItemId),
          invoiceId: String(invoiceId),
          unitPrice,
          currency,

        }, shouldIncludeOverrideReason ? String(result?.overrideReason ?? '').trim() : undefined)
        .subscribe({
          next: ({ invoice, chargeItem, unitPriceValue, currency: ccy }) => {
            const patchDs = (ds: MatTableDataSource<any>) => {
              const current = ds.data || [];
              ds.data = current.map((r) => {
                const rid = this.extractInvoiceId(r);
                const rcid = (r?.relatedChargeItem as ChargeItem | undefined)?.id ?? null;
                if (rid !== invoiceId || String(rcid) !== String(chargeItemId)) return r;

                return {
                  ...r,
                  relatedChargeItem: chargeItem || r.relatedChargeItem,
                  currency: ccy || r.currency || 'NGN',
                  unitPriceValue: unitPriceValue,
                  // legacy key (print fallback)
                  'unit price': unitPriceValue,
                  totalGross: (invoice as any)?.totalGross,
                  totalGrossDisplay: this.fmtMoney((invoice as any)?.totalGross),
                };
              });
            };

            for (const ds of this.tableDataSourceMap.values()) patchDs(ds);
            patchDs(this.medicationInvoiceRows);
            patchDs(this.labInvoiceRows);
            patchDs(this.otherServiceInvoiceRows);
          },
          error: (err) => {
            console.error('Failed to apply manual price', err);
            this.errorService.openandCloseError(err?.message ? String(err.message) : 'Failed to apply price.');
          }
        });
    });
  }

  private deriveInvoiceSectionFromChargeItem(ci: ChargeItem): 'DiagnosticReport' | 'ImagingStudy' | 'Immunization' | 'MedicationAdministration' | 'MedicationDispense' | 'Observation' | 'Procedure' | 'SupplyDelivery' | 'Laboratory' | 'Others' | 'Surgery' {
    let pr = ((ci as any)?.service?.[0]?.reference?.split('/')[(ci.service?.[0].reference?.split('/')?.length || 0) - 2] ??
      (ci as any)?.productReference?.reference?.split('/')[(ci.productReference?.reference?.split('/')?.length || 0) - 2] ?? ci.code.coding?.[0]?.code?.toString().toLowerCase() ?? ''
    ).toString();
    if (!pr) {
      //then check code
    }
    pr = pr.toString().toLowerCase();

    // Medication Dispense pattern: productReference points to Medication/...
// $#$    // /* alert('pr' + pr); */
    if (ci.service && ci.service.length > 0 && (
      !(ci.service[0].reference ?? '').toString().toLowerCase().includes('diagnosticreport/')
      // and/or Observation
      && !(ci.service[0].reference ?? '').toString().toLowerCase().includes('observation/')
      // and/or imagingstudy
      && !(ci.service[0].reference ?? '').toString().toLowerCase().includes('imagingstudy/')
      // and/or procedure
      && !(ci.service[0].reference ?? '').toString().toLowerCase().includes('procedure/')
    )) {

      const key = (ci.service[0].reference?.split('/')[ci.service[0].reference?.split('/').length - 2] ?? '').toString().trim();
// $#$      // /* alert('key' + key); */
      if (key.toLowerCase() === 'other' || key.toLowerCase() === 'others') return 'Others';
      return key as any;
    }

    // Laboratory: usually ChargeItem is for an Observation (lab result) or a lab ServiceRequest (if you front-load billing)
    // if (pr.startsWith('lab')) return 'Laboratory';
    if (pr.includes('medication')) return 'MedicationDispense';
// $#$    // /* alert('pr' + pr); */
    if (pr.includes('diagnosticreport') ||
      pr.includes('observation') ||
      pr.includes('imagingstudy') ||
      pr.includes('procedure')
    ) {
      //check the code
      if (ci.code && ci.code.coding && ci.code.coding.length > 0

        && (ci.code.coding[0].code?.split('-') || []).length > 1
      ) {
// $#$        // /* alert('code' + ci.code.coding[0].code); */
        const codePart = (ci.code.coding[0].code?.split('-')[0] || '').toString().toLowerCase();
// $#$        // /* alert(codePart) */
        return codePart as 'DiagnosticReport' | 'ImagingStudy' | 'Immunization' | 'MedicationAdministration' | 'MedicationDispense' | 'Observation' | 'Procedure' | 'SupplyDelivery' | 'Laboratory' | 'Others' | 'Surgery';

      }
    }


    return 'Others';
  }

  private extractChargeItemIdFromRef(ref?: string): string | null {
    const r = (ref ?? '').toString().trim();
    if (!r) return null;
    if (r.startsWith('ChargeItem/')) return r.split('/').pop() || null;
    return r.includes('/') ? (r.split('/').pop() || null) : r;
  }

  private extractProductLabelFromChargeItem(ci: ChargeItem): string {
    const pr: any = (ci as any)?.productReference;
    if (pr) return pr.display || pr.reference || 'N/A';

    const codeText = (ci as any)?.code?.text;
    if (codeText) return String(codeText);

    const codingDisplay = (ci as any)?.code?.coding?.[0]?.display;
    if (codingDisplay) return String(codingDisplay);

    const codingCode = (ci as any)?.code?.coding?.[0]?.code;
    if (codingCode) return String(codingCode).split('-')[1] || String(codingCode);

    return 'N/A';
  }

  private normalizeSectionKey(input?: string | null): 'DiagnosticReport' | 'ImagingStudy' | 'Immunization' | 'MedicationAdministration' | 'MedicationDispense' | 'Observation' | 'Procedure' | 'SupplyDelivery' | 'Laboratory' | 'Others' {
    const raw = (input ?? '').toString().trim();
    if (!raw) return 'Others';
    if (raw.toLowerCase() === 'other' || raw.toLowerCase() === 'others') return 'Others';
    return raw as any;
  }

  private newUuid(): string {
    try {
      // Browser-native when available
      return (globalThis as any)?.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  private buildRowFromCreatedInvoiceAndChargeItem(inv: Invoice, related: ChargeItem): (Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>) {
    const qty = (related as any)?.quantity?.value ?? (related as any)?.quantity ?? null;
    const status = (inv.status ?? '').toString();

    const dateRaw = (inv as any)?.date ?? (inv as any)?.meta?.lastUpdated ?? '';
    const d = dateRaw ? new Date(dateRaw) : null;
    const section = this.deriveInvoiceSectionFromChargeItem(related);
// $#$    // /* alert('section' + section); */
    const label = this.extractProductLabelFromChargeItem(related);

    const currency = ((inv as any)?.totalGross?.currency ?? 'NGN').toString() || 'NGN';
    const overrideUnit = Number((related as any)?.priceOverride?.value);
    const overrideUnitValid = Number.isFinite(overrideUnit);
    const unitPriceValue = overrideUnitValid ? overrideUnit : null;
// $#$    // /* alert(inv.totalGross); */
    return {
      ...(inv as any),
      relatedChargeItem: related,
      'Invoice_Identifier': inv.identifier?.find((id: any) => id.system === 'https://elikita-server.daalitech.com/fhir/identifier/inv')?.value || (inv.id ? `INV-${inv.id.substring(0,6).toUpperCase()}` : 'Invoice'),
      invoiceIdDisplay: inv.id ? `Invoice/${inv.id}` : 'Invoice',
      invoiceDateDisplay: d ? d.toLocaleString() : 'N/A',
      invoiceDayDisplay: d ? d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
      invoiceTimeDisplay: d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
      invoiceStatusDisplay: status || 'N/A',
      ...(section === 'MedicationDispense'
        ? { medicationDisplay: label }
        : { serviceDisplay: label }
      ),
      quantityDisplay: qty != null ? String(qty) : 'N/A',
      currency,
      unitPriceValue: Number.isFinite(unitPriceValue as any) ? unitPriceValue : null,
      'unit price': Number.isFinite(unitPriceValue as any) ? unitPriceValue : 'N/A',
      totalGrossDisplay: (inv as any)?.totalGross ? this.fmtMoney((inv as any)?.totalGross) : 'N/A',
    };
  }

  private buildInvoiceDetailsBuilder(): DetailsBuilderObject {
    return {
      resourceName: 'Invoice',
      resourceIcon: 'receipt_long',
      specialHeader: {
        strongSectionKey: 'Invoice_Identifier',
        iconSectionKeys: ['invoiceStatusDisplay'],
        contentSectionKeys: ['invoiceDateDisplay', 'medicationDisplay', 'totalGrossDisplay']
      },
      groups: [
        { groupName: 'Invoice', groupIcon: 'receipt_long', groupKeys: ['invoiceIdDisplay', 'invoiceStatusDisplay', 'invoiceDateDisplay', 'totalGrossDisplay'] },
        { groupName: 'Charge Item', groupIcon: 'payments', groupKeys: ['chargeItemIdDisplay', 'chargeItemContextDisplay', 'dispenseIdDisplay', 'chargeItemProductDisplay', 'quantityDisplay'] }
      ]
    };
  }

  async viewInvoiceRow(row: any): Promise<void> {
    const ci = row?.relatedChargeItem as ChargeItem | undefined;

    let dispenseIdDisplay = 'N/A';
    const ciServiceRef = ci?.service?.[0]?.reference;
    if (ciServiceRef && ciServiceRef.toLowerCase().includes('medicationdispense')) {
      try {
        dispenseIdDisplay = await firstValueFrom(this.referenceDisplayService.ensureIdentifier(ciServiceRef));
      } catch {
        // ignore
      }
    }

    const resourceData = {
      ...row,
      resourceType: 'Invoice',
      chargeItemIdDisplay: ci?.id ? `ChargeItem/${ci.id}` : 'N/A',
      chargeItemContextDisplay: (ci as any)?.context?.reference || 'N/A',
      dispenseIdDisplay,
      chargeItemProductDisplay: this.extractProductLabelFromChargeItem(ci as any),
    };

    this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '90vw',
      data: {
        resourceData,
        detailsBuilderObject: this.buildInvoiceDetailsBuilder(),
        excludeKeys: ['relatedChargeItem']
      }
    });
  }

  // Keep backwards compatibility if your template still calls viewMedicationInvoiceRow
  viewMedicationInvoiceRow(row: any): void {
    this.viewInvoiceRow(row);
  }

  // CHANGED: update status in all 3 data sources so UI stays consistent
  markInvoiceAsPaid(row: any): void {
    const invoiceId = (row as Invoice)?.id;
    if (!invoiceId) {
      this.errorService.openandCloseError('Invoice id is missing.');
      return;
    }

    let fetchedInvoiceSnapshot: Invoice | null = null;

    const patchUi = (nextStatus: string) => {
      const patchDs = (ds: MatTableDataSource<any>) => {
        const current = ds.data || [];
        ds.data = current.map(r => {
          if ((r as any)?.id !== invoiceId) return r;
          return { ...r, status: nextStatus, invoiceStatusDisplay: nextStatus };
        });
      };

      for (const ds of this.tableDataSourceMap.values()) {
        patchDs(ds);
      }
      patchDs(this.medicationInvoiceRows);
      patchDs(this.labInvoiceRows);
      patchDs(this.otherServiceInvoiceRows);
    };

    const markPaidStatusOnly = (invoiceSnapshot?: Invoice | null) => {
      const source$ = invoiceSnapshot
        ? of(invoiceSnapshot)
        : this.http.get<Invoice>(`${this.backendApiEndPoint}/Invoice/${invoiceId}`, {
          headers: { 'Accept': 'application/fhir+json' }
        });

      return source$.pipe(
        switchMap((inv) => {
          if (!inv) {
            throw new Error('Invoice not found');
          }
          const updatedInvoice: Invoice = { ...inv, status: 'balanced' };
          return this.http.put(
            `${this.backendApiEndPoint}/Invoice/${invoiceId}`,
            updatedInvoice,
            {
              headers: {
                'Content-Type': 'application/fhir+json',
                'Accept': 'application/fhir+json',
                'Prefer': 'return=representation'
              }
            }
          ).pipe(map((updated: any) => ({ updated })));
        })
      );
    };

    this.http.get<Invoice>(`${this.backendApiEndPoint}/Invoice/${invoiceId}`, {
      headers: {
        'Accept': 'application/fhir+json',
        // Best-effort: ask intermediaries/server not to serve cached content.
        // (Some servers ignore this, but it helps when a proxy/browser cache is involved.)
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      // Frontend in-memory cache bypass
      context: new HttpContext().set(DisableCache, true)
    }).pipe(
      switchMap((inv) => {
        fetchedInvoiceSnapshot = inv;
        console.log('Fetched invoice for marking as paid', inv);
// $#$        // /* debugger; */

        const now = new Date();
        const created = now.toISOString();
        const paymentDate = created.slice(0, 10);

        const currency = (
          (inv as any)?.totalGross?.currency ??
          (inv as any)?.totalNet?.currency ??
          (row as any)?.totalGross?.currency ??
          (row as any)?.totalNet?.currency ??
          (row as any)?.currency ??
          'NGN'
        ).toString();

        const amountValue = Number(
          (inv as any)?.totalGross?.value ??
          (inv as any)?.totalNet?.value ??
          (row as any)?.totalGross?.value ??
          (row as any)?.totalNet?.value ??
          0
        );

        const paymentAmount = {
          value: Number.isFinite(amountValue) ? amountValue : 0,
          currency
        };

        // PaymentNotice.recipient is required (Organization).
        // Prefer Invoice.issuer, otherwise fall back to the logged-in user's hospital org.
        const authUser: any = this.auth.user.getValue();
        const authOrgRef = authUser?.hospitalReference || (authUser?.hospitalId ? `Organization/${authUser.hospitalId}` : null);
        const recipient = ((inv as any)?.issuer?.reference
          ? (inv as any)?.issuer
          : (authOrgRef ? { reference: authOrgRef } : null)
        );
        const canCreatePaymentNotice = !!recipient?.reference;

        const updatedInvoice: Invoice = {
          ...(inv as any),
          status: 'balanced'
        };

        const paymentReconciliationFullUrl = `urn:uuid:${this.newUuid()}`;
        const paymentReconciliation: PaymentReconciliation = {
          resourceType: 'PaymentReconciliation',
          status: 'active',
          created,
          paymentDate,
          paymentAmount,
          detail: [
            {
              type: {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/payment-type',
                    code: 'payment',
                    display: 'Payment'
                  }
                ],
                text: 'Payment'
              },
              request: { reference: `Invoice/${invoiceId}` },
              date: paymentDate,
              amount: paymentAmount,
              ...(recipient?.reference ? { payee: recipient } : {})
            }
          ]
        };

        const entries: any[] = [
          {
            request: { method: 'PUT', url: `Invoice/${invoiceId}` },
            resource: updatedInvoice
          },
          {
            fullUrl: paymentReconciliationFullUrl,
            request: { method: 'POST', url: 'PaymentReconciliation' },
            resource: paymentReconciliation
          }
        ];

        if (canCreatePaymentNotice) {
          const paymentNotice: PaymentNotice = {
            resourceType: 'PaymentNotice',
            status: 'active',
            created,
            payment: { reference: paymentReconciliationFullUrl },
            recipient,
            amount: paymentAmount
          };

          entries.push({
            fullUrl: `urn:uuid:${this.newUuid()}`,
            request: { method: 'POST', url: 'PaymentNotice' },
            resource: paymentNotice
          });
        }

        const bundle: Bundle = {
          resourceType: 'Bundle',
          type: 'transaction',
          entry: entries
        };

        console.log('Submitting payment transaction bundle', bundle);
// $#$        // /* alert(JSON.stringify(bundle, null, 2) */);
        return this.http.post<any>(
          `${this.backendApiEndPoint}`,
          bundle,
          {
            headers: {
              'Content-Type': 'application/fhir+json',
              'Accept': 'application/fhir+json',
              'Prefer': 'return=representation'
            }
          }
        ).pipe(
          map((txnResponse: any) => ({ txnResponse, canCreatePaymentNotice })),
        );
      }),
      catchError((err) => {
        console.error('Failed to create payment resources in transaction; falling back to status-only update.', err);
        return markPaidStatusOnly(fetchedInvoiceSnapshot).pipe(
          catchError((err2) => {
            console.error('Failed to mark invoice as paid (status-only).', err2);
            this.errorService.openandCloseError('Failed to mark invoice as paid.');
            return of(null);
          })
        );
      })
    ).subscribe((result: any) => {
      if (!result) return;

      // Prefer returned representation; otherwise assume balanced.
      const updated = result?.updated;
      const nextStatus = (updated?.status ?? 'balanced').toString();
      patchUi(nextStatus);

      if (result?.canCreatePaymentNotice === false) {
        // Not fatal; we still created a PaymentReconciliation and balanced the invoice.
        console.warn('PaymentNotice not created because Invoice.issuer is missing.');
      }
    });
  }

  ngOnInit() {
    // --- NEW: query params driving patient/encounter context (similar to what you already started) ---
    const qp = this.utilityService.getAllQueryParamsNormalized(this.route);
    const qpPatient = qp['patientId'] as string;
    const qpEncounter = qp['encounterId'] as string;

    if (qpPatient) this.patientId = String(qpPatient);
    if (qpEncounter) this.encounterId = String(qpEncounter);

    //get invoice related to this encounter, reactively reloading on param or query param changes
    combineLatest([this.route.params, this.route.queryParams])
      .pipe(
        switchMap(([e, qparams]) => {
          const encounterId = (e['encounterId'] ?? qparams['encounterId'] ?? this.encounterId) as string;
          const patientId = (e['patientId'] ?? qparams['patientId'] ?? this.patientId) as string;

          if (!encounterId || !patientId) {
            this.errorService.openandCloseError('You must provide a patient and encounter parameter to view this page.');
            window.history.back();
            return of({ invoices: [], chargeItem: [], ChargeItemDefinitionMentioned: [], qp: qparams });
          }

          this.encounterId = encounterId;
          this.patientId = patientId;

          // Fetch ChargeItems FIRST so we know exactly which Invoices we need
          return this.utilityService.fetchAll<ChargeItem>({
            resourceType: 'ChargeItem',
            queries: {
              subject: `Patient/${patientId}`,
              context: `Encounter/${encounterId}`
            },
            checkOrgWideState: true,
            updateOrgWideState: true,
            localFilters: [
              { key: 'subject.reference', value: `Patient/${patientId}` },
              { key: 'context.reference', value: `Encounter/${encounterId}` }
            ]
          }).pipe(
            map(items => items.filter(ci => !!ci?.id)),
            catchError(() => of([] as ChargeItem[])),
            switchMap(chargeItems => {
              // Extract the raw IDs from the definitionCanonical URLs
              const referencedUrls = new Set((chargeItems || []).map((ci) => ci.definitionCanonical || []).flat().filter(Boolean));
              const idsToFetch = Array.from(referencedUrls).map(url => url.split('/').pop()).filter(Boolean);
              
              const chargeItemDefFetch$ = idsToFetch.length > 0
                ? this.utilityService.fetchAll<ChargeItemDefinition>({
                    resourceType: 'ChargeItemDefinition',
                    queries: { _id: idsToFetch.join(',') },
                    // We skip org-wide state here because this is a highly specific query
                    // and we don't want to expect _summary=count to match the global catalog size.
                    checkOrgWideState: false, 
                    updateOrgWideState: false
                  }).pipe(
                    map(all => {
                      console.log('Fetched ChargeItemDefinitions by _id', all);
                      return all;
                    }),
                    catchError(() => of([] as ChargeItemDefinition[]))
                  )
                : of([] as ChargeItemDefinition[]);

              // Now fetch Invoices and ChargeItemDefinitions in parallel
              return forkJoin({
                chargeItem: of(chargeItems),
                invoices: this.utilityService.fetchAll<Invoice>({
                  resourceType: 'Invoice',
                  queries: {
                    subject: `Patient/${patientId}`,
                    _sort: '-date'
                  },
                  checkOrgWideState: true,
                  updateOrgWideState: true,
                  localFilters: [
                    { key: 'subject.reference', value: `Patient/${patientId}` }
                  ],
                  stopWhen: (fetchedInvoices) => {
                    if (!chargeItems.length) return true; // nothing to look for
                    return chargeItems.every(ci => 
                      fetchedInvoices.some(inv => 
                        ['issued', 'balanced'].includes((inv.status ?? '').toLowerCase()) && 
                        inv.lineItem?.some(li => li.chargeItemReference?.reference?.includes(ci.id!))
                      )
                    );
                  }
                }).pipe(
                  map(invoices => invoices.filter(inv => ['issued', 'balanced'].includes((inv.status ?? '').toLowerCase()))),
                  catchError(() => of([] as Invoice[]))
                ),
                ChargeItemDefinition: chargeItemDefFetch$,
              });
            })
          ).pipe(
            map(({ invoices, chargeItem, ChargeItemDefinition }) => {
              console.log('Processing invoices and charge items for encounter', ({ invoices, chargeItem, ChargeItemDefinition }));
              const chargeItemIds = new Set((chargeItem || []).map((ci) => ci.id!).filter(Boolean));
              const chargeItemReferencedIds = new Set((chargeItem || []).map((cid: ChargeItem) => cid.definitionCanonical || []).flat().filter(Boolean));

              // Only include invoices that contain any of the encounter charge items
              const invoicesForEncounter = (invoices || []).filter(inv => {
                const lineItems = (inv as any)?.lineItem ?? [];
                return lineItems.some((li: any) => {
                  const ref = li?.chargeItemReference?.reference;
                  const id = this.extractChargeItemIdFromRef(ref);
                  console.log('Checking invoice', inv.id, 'line item chargeItemReference', ref, 'extracted id', id);
                  return !!id && chargeItemIds.has(id);
                });
              });

              const ChargeItemDefinitionMentioned = (ChargeItemDefinition || []).filter(cid => {
                return chargeItemReferencedIds.has(`${this.backendApiEndPoint}/ChargeItemDefinition/${cid.id}`);
              })

              return { invoices: invoicesForEncounter, chargeItem, ChargeItemDefinitionMentioned, qp: qparams };
            })
          );
        })).subscribe(({ invoices, chargeItem, ChargeItemDefinitionMentioned, qp }) => {
          // console.log('Subscription received data', { invoices, chargeItem, ChargeItemDefinitionMentioned });
          this.tableDataSourceMap.clear();
          // Build combined rows: invoice + its related charge item(s)
          const rows: (Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>)[] = [];

          (invoices || []).forEach((inv: Invoice) => {
            console.log('Processing invoice', inv.id);
            const lineItems = (inv as any)?.lineItem ?? [];
            lineItems.forEach((li: any) => {
              const ref = li?.chargeItemReference?.reference;
              const id = this.extractChargeItemIdFromRef(ref);
              if (!id) return;
              const related = (chargeItem || []).find(ci => ci.id === id);
              if (!related) return;

              const qty = (related as any)?.quantity?.value ?? (related as any)?.quantity ?? null;
              const status = (inv.status ?? '').toString();

              const dateRaw = (inv as any)?.date ?? (inv as any)?.meta?.lastUpdated ?? '';
              const d = dateRaw ? new Date(dateRaw) : null;

              // CHANGED: when building rows, set section-specific display fields (like med dispense)
              const section = this.deriveInvoiceSectionFromChargeItem(related);
              const label = this.extractProductLabelFromChargeItem(related);
              const relatedDef = ChargeItemDefinitionMentioned.find(cid => {
                return (related.definitionCanonical || []).includes(`${this.backendApiEndPoint}/ChargeItemDefinition/${cid.id}`);
              });
              console.log('Related ChargeItemDefinition for ChargeItem', related.id, 'is', relatedDef?.id);
              const unitPrice = relatedDef ? this.utilityService.seperateTotalPriceComponents(relatedDef, false) : null;
              console.log('Unit price extracted for ChargeItem', related.id, 'is', unitPrice);
              const filterType = (qp?.['type'] ?? '').toString().toLowerCase();
              if (section?.toLowerCase() === filterType || !filterType || filterType === 'all') {
                const currency = ((inv as any)?.totalGross?.currency ?? 'NGN').toString() || 'NGN';
                const overrideUnit = Number((related as any)?.priceOverride?.value);
                const overrideUnitValid = Number.isFinite(overrideUnit);
                const defUnit = unitPrice ? Number(unitPrice['totalSellingPrice'] ?? unitPrice['totalPrice'] ?? unitPrice) : NaN;
                const unitPriceValue = overrideUnitValid ? overrideUnit : (Number.isFinite(defUnit) ? defUnit : null);
                rows.push({
                  ...(inv as any),
                  relatedChargeItem: related,
                    'Invoice_Identifier': inv.identifier?.find((id: any) => id.system === 'https://elikita-server.daalitech.com/fhir/identifier/inv')?.value || (inv.id ? `INV-${inv.id.substring(0,6).toUpperCase()}` : 'Invoice'),

                  invoiceIdDisplay: inv.id ? `Invoice/${inv.id}` : 'Invoice',
                  invoiceDateDisplay: d ? d.toLocaleString() : 'N/A',
                  invoiceDayDisplay: d ? d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
                  invoiceTimeDisplay: d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
                  invoiceStatusDisplay: status || 'N/A',
                  ...(section === 'MedicationDispense'
                    ? { medicationDisplay: label }
                    : { serviceDisplay: label }
                  ),
                  quantityDisplay: qty != null ? String(qty) : 'N/A',
                  currency,
                  unitPriceValue: Number.isFinite(unitPriceValue as any) ? unitPriceValue : null,
                  'unit price': Number.isFinite(unitPriceValue as any) ? unitPriceValue : 'N/A',
                  totalGrossDisplay: (inv as any)?.totalGross ? this.fmtMoney((inv as any)?.totalGross) : 'N/A',
                });
              }
            });
          });

          rows.forEach((e: any) => {

            const section = this.deriveInvoiceSectionFromChargeItem(e.relatedChargeItem);
// $#$            // /* alert(section); */

            const ds =
              this.tableDataSourceMap.get(section) ??
              new MatTableDataSource<Invoice & { relatedChargeItem: ChargeItem } & Record<string, any>>([]);

            ds.data = [...(ds.data ?? []), e];
            this.tableDataSourceMap.set(section, ds);
          });


          // const med = rows.filter(r => this.deriveInvoiceSectionFromChargeItem(r.relatedChargeItem) === 'MedicationDispense');
          // const lab = rows.filter(r => this.deriveInvoiceSectionFromChargeItem(r.relatedChargeItem) === 'Laboratory');

          // const other = rows.filter(r => {
          //   const section = this.deriveInvoiceSectionFromChargeItem(r.relatedChargeItem);
          //   return section !== 'MedicationDispense' && section !== 'Laboratory';
          // });

          // this.medicationInvoiceRows.data = med;
          // this.labInvoiceRows.data = lab;
          // this.otherServiceInvoiceRows.data = other;
        });




    forkJoin({
      currency: this.formFieldService.getFormFieldSelectData('chargeItemDef', 'currency'),
      labCode: this.formFieldService.getFormFieldSelectData('serviceRequest', 'code'),
      code: this.formFieldService.getFormFieldSelectData('medication', 'code')

    })
      .subscribe((g: any) => {
        this.chargesFormFields = new Map([


          [
            'productServiceType', {
              formFields: <FormFields[]>[


                <GroupField>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'product_service_resourceType',
                    fieldName: "Product/Service Type",
                    fieldType: 'SingleCodeField',
                    inputType: 'text',
                    isArray: false,
                    isGroup: true,

                  },
                  keys: ['product_service', 'other_product_service'],

                  groupFields: {
                    product_service:
                      <SingleCodeField>{
                        generalProperties: <generalFieldsData>{
                          auth: {
                            read: 'all',
                            write: 'doctor, nurse'
                          },
                          fieldApiName: 'product_service_resourceType',
                          fieldName: "Product/Service Type",
                          fieldType: 'SingleCodeField',
                          inputType: 'text',
                          isArray: false,
                          isGroup: false,
                          controllingField: [{

                            isAControlField: true,
                            controlledFieldDependencyId: "pp.pp1",
                            dependentFieldVisibilityTriggerValue: "Others"
                          }]

                        },
                        data: ['Consultation', 'Medication Dispense', 'Lab Test', 'Procedure', 'Imaging',
                          'Immunization', 'Medication Administration', 'Diagnostic Report', "Others"],
                      },




                    'other_product_service':
                      <IndividualField>{
                        generalProperties: <generalFieldsData>{
                          auth: {
                            read: 'all',
                            write: 'doctor, nurse'
                          },
                          fieldApiName: 'other_product_service',
                          fieldName: "Other Product/Service Type",
                          fieldType: 'IndividualField',
                          inputType: 'text',
                          isArray: false,
                          isGroup: false,

                        },
                        data: '',
                      },


                  }

                }
                // DiagnosticReport | ImagingStudy | Immunization | MedicationAdministration | MedicationDispense | MedicationRequest | Observation | Procedure | ServiceRequest | SupplyDelivery
              ],
            },
          ],
          [
            'code', {
              formFields: <FormFields[]>[
                <CodeableConceptField>{
                  generalProperties: <generalFieldsData>{
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldApiName: 'medicine_code',
                    fieldName: "Medicine Name",
                    fieldType: 'CodeableConceptFieldFromBackEnd',
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,

                  },
                  data: g.code
                },
              ]
            }
          ],
          //labcode
          ['labCode', {
            formFields: <FormFields[]>[{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'labCode',
                fieldName: "Lab Test Code",
                fieldType: 'CodeableConceptFieldFromBackEnd',
                inputType: 'text',
                isArray: false,
                isGroup: false,
                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
              data: g.labCode
            }]
          }],


          ['currency', {
            formFields: <FormFields[]>[{
              generalProperties: <generalFieldsData>{
                fieldApiName: 'currency',
                fieldName: "Currency",
                value: "NGN",
                fieldType: 'CodeableConceptField',
                inputType: 'text',
                isArray: false,
                isGroup: false,
                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
              },
              data: g.currency
            },
            ],
          }],

          ['type', {
            formFields: <FormFields[]>[
              <SingleCodeField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'type',
                  fieldName: "Type",
                  fieldType: 'SingleCodeField',
                  inputType: 'text',
                  value: 'base',
                  isArray: false,
                  isGroup: false,
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
                data: 'base | surcharge | deduction | tax'.split(' | ')
              }]
          }],





          [
            'price', {
              formFields: <FormFields[]>[


                <IndividualField>{
                  generalProperties: <generalFieldsData>{
                    fieldApiName: 'price',
                    fieldName: "Price",
                    fieldType: 'IndividualField',
                    inputType: 'number',
                    isArray: false,
                    isGroup: false,
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                  },
                }





              ]
            }

          ],























        ])
      })
    this.incomingQueryParams = this.utilityService.getAllQueryParamsNormalized(this.route);
// $#$    // /* alert('Invoice route query params:'
    //   + JSON.stringify(this.incomingQueryParams) */);
    this.isAnyQueryParams = Object.keys(this.incomingQueryParams || {}).length > 0;
    this.route.queryParams.subscribe(params => {
      this.incomingQueryParams = params;
      this.isAnyQueryParams = Object.keys(params || {}).length > 0;
    });
  }
  incomingQueryParams?: any
  isAnyQueryParams = false;
  route = inject(ActivatedRoute);
  router = inject(Router);

  seeEncounterSummary() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { type: 'all' },
      queryParamsHandling: 'merge'
    });
  }


  dialog = inject(MatDialog);
  errorService = inject(ErrorService);

  addAnyCharges() {
    if (this.chargesFormFields) {
      this.dialog.open(DynamicFormsV2Component, {
        width: '650px',
        data: {
          formMetaData: {
            formName: 'Patient Charges Form',
            formDescription: 'Form to add charges for a patient encounter',
            submitText: 'Add Charges',
            showSubmitButton: true

          } as formMetaData,
          formFields: this.utilityService.convertFormFields(this.chargesFormFields).filter(field => field.generalProperties.fieldApiName !== 'labCode' && field.generalProperties.fieldApiName !== 'medicine_code'),
          // Pass any data you need to the dialog here
        }
      }).afterClosed().subscribe(result => {
        if (result) {
          // Handle the result from the dialog
        }
      });
    }
  }

  addManualCharge(sectionKey?: string): void {
    if (!this.chargesFormFields) {
      this.errorService.openandCloseError('Charge form is not ready yet. Please wait a moment and try again.');
      return;
    }

    const targetSection = this.normalizeSectionKey(sectionKey);

    const currencyField = this.utilityService
      .convertFormFields(this.chargesFormFields)
      .find((f: any) => f?.generalProperties?.fieldApiName === 'currency');

    const formFields = [
      <any>{
        generalProperties: {
          fieldApiName: 'manualChargeCodeText',
          fieldName: 'What are you charging for?',
          fieldLabel: 'What are you charging for?',
          fieldType: 'IndividualField',
          inputType: 'text',
          isArray: false,
          isGroup: false,
          value: ''
        }
      },
      <any>{
        generalProperties: {
          fieldApiName: 'quantity',
          fieldName: 'Quantity',
          fieldLabel: 'Quantity',
          fieldType: 'IndividualField',
          inputType: 'number',
          isArray: false,
          isGroup: false,
          value: 1
        }
      },
      ...(currencyField ? [currencyField] : []),
      <any>{
        generalProperties: {
          fieldApiName: 'price',
          fieldName: 'Unit Price',
          fieldLabel: 'Unit Price',
          fieldType: 'IndividualField',
          inputType: 'number',
          isArray: false,
          isGroup: false,
          value: ''
        }
      }
    ];

    this.dialog.open(DynamicFormsV2Component, {
      width: '650px',
      data: {
        formMetaData: <formMetaData>{
          formName: `Add Manual Charge${targetSection ? ` (${targetSection})` : ''}`,
          formDescription: 'Creates an ad-hoc charge item and invoice for this encounter.',
          submitText: 'Add Charge',
          showSubmitButton: true,
          closeDialogOnSubmit: true

        } as formMetaData,
        formFields
      }
    }).afterClosed().subscribe((result: any) => {
      if (!result) return;
      result = result.values
      const codeText = String(result?.manualChargeCodeText ?? '').trim();
      if (!codeText) {
        this.errorService.openandCloseError('Please enter a charge code/description.');
        return;
      }

      const qtyRaw = Number(result?.quantity ?? 1);
      const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;

      const unitPrice = Number(result?.price);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
        this.errorService.openandCloseError('Please provide a valid unit price.');
        return;
      }

      const currency = this.coerceCurrencyCode(result?.currency);
      const total = unitPrice * quantity;
      const nowIso = new Date().toISOString();

      const chargeItemFullUrl = `urn:uuid:${this.newUuid()}`;
      const invoiceFullUrl = `urn:uuid:${this.newUuid()}`;
      const organizationId = this.auth.getOrganizationId();

      const chargeItem: ChargeItem = {
        resourceType: 'ChargeItem',
        status: 'billable' as any,
        code: { text: codeText } as any,
        subject: { reference: `Patient/${this.patientId}` },
        context: { reference: `Encounter/${this.encounterId}` },
        enteredDate: nowIso,
        occurrenceDateTime: nowIso,
        quantity: { value: quantity } as any,
        priceOverride: { value: unitPrice, currency } as any,
        ...(organizationId ? { performingOrganization: { reference: `Organization/${organizationId}` } } : {}),
        ...(targetSection && targetSection !== 'Others'
          ? { service: [{ reference: `${targetSection}/manual`, display: targetSection }] as any }
          : {})
      } as any;

      const invoice: Invoice = {
        resourceType: 'Invoice',
        status: 'issued' as any,
        subject: { reference: `Patient/${this.patientId}` },
        date: nowIso,
        lineItem: [
          {
            sequence: 1,
            chargeItemReference: { reference: chargeItemFullUrl },
            priceComponent: [
              { type: 'base', amount: { value: total, currency } as any }
            ]
          } as any
        ] as any,
        totalPriceComponent: [
          { type: 'base', amount: { value: total, currency } as any }
        ] as any,
        totalNet: { value: total, currency } as any,
        totalGross: { value: total, currency } as any,
      } as any;

      const bundle: any = {
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          { fullUrl: chargeItemFullUrl, resource: chargeItem, request: { method: 'POST', url: 'ChargeItem' } },
          { fullUrl: invoiceFullUrl, resource: invoice, request: { method: 'POST', url: 'Invoice' } }
        ]
      };

      this.http.post<any>(`${this.backendApiEndPoint}`, bundle, {
        headers: { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' }
      }).pipe(
        catchError((err) => {
          console.error('Failed to add manual charge', err);
          this.errorService.openandCloseError('Failed to add manual charge.');
          return of(null);
        })
      ).subscribe((resp: any) => {
        if (!resp) return;

        const entries: any[] = Array.isArray(resp?.entry) ? resp.entry : [];
        const createdChargeItem = (entries.map(e => e?.resource).find(r => r?.resourceType === 'ChargeItem') ?? null) as ChargeItem | null;
        const createdInvoice = (entries.map(e => e?.resource).find(r => r?.resourceType === 'Invoice') ?? null) as Invoice | null;

        if (!createdChargeItem || !createdInvoice) {
          // If server doesn't echo resources, fall back to refresh via route params by reloading page data on next navigation.
          this.errorService.openandCloseError('Charge created, but could not refresh view automatically. Please refresh this page.');
          return;
        }

        const row = this.buildRowFromCreatedInvoiceAndChargeItem(createdInvoice, createdChargeItem);
        const section = this.deriveInvoiceSectionFromChargeItem(createdChargeItem);

        const ds = this.tableDataSourceMap.get(section) ?? new MatTableDataSource<any>([]);
        ds.data = [...(ds.data ?? []), row];
        this.tableDataSourceMap.set(section, ds);
      });
    });
  }

  addMedicationCharges() {
    if (this.chargesFormFields) {
      this.dialog.open(DynamicFormsV2Component, {
        width: '650px',
        data: {
          formMetaData: {
            formName: 'Medication Charges Form',
            formDescription: 'Form to add medication charges for a patient encounter',
            submitText: 'Add Medication Charges',
            showSubmitButton: true
          } as formMetaData,
          formFields: this.utilityService.convertFormFields(this.chargesFormFields).filter(field => field.generalProperties.fieldApiName !== 'product_service_resourceType' && field.generalProperties.fieldApiName !== 'labCode')
          // Pass any data you need to the dialog here
        }
      }).afterClosed().subscribe(result => {
        if (result) {
          // Handle the result from the dialog
        }
      });
    }
  }

  changePrice(prevVal: any = 0) {
    if (this.chargesFormFields) {
      this.dialog.open(DynamicFormsV2Component, {
        width: '650px',
        data: {
          formMetaData: {
            formName: 'Price Change Form',
            formDescription: '',
            submitText: prevVal == 0 ? 'Add Price' : 'Update Price',
            showSubmitButton: true
          } as formMetaData,
          formFields: this.utilityService.convertFormFields(this.chargesFormFields).filter((field) => {
            return field.generalProperties.fieldApiName !== 'product_service_resourceType' && field.generalProperties.fieldApiName !== 'labCode'
              && field.generalProperties.fieldApiName !== 'medicine_code'
          }
          ).map((field) => {
            if (field.generalProperties.fieldApiName === 'price') {
              field.generalProperties.value = prevVal;
            }
            return field;
          })
          // Pass any data you need to the dialog here
        }
      }).afterClosed().subscribe(result => {
        if (result) {
          // Handle the result from the dialog
        }
      });
    }

  }

  //addLabCharges() {
  addLabCharges() {
    if (this.chargesFormFields) {
      this.dialog.open(DynamicFormsV2Component, {
        width: '650px',
        data: {
          formMetaData: {
            formName: 'Lab Charges Form',
            formDescription: 'Form to add lab charges for a patient encounter',
            submitText: 'Add Lab Charges',
            showSubmitButton: true
          } as formMetaData,
          formFields: this.utilityService.convertFormFields(this.chargesFormFields).filter(field => field.generalProperties.fieldApiName !== 'product_service_resourceType' && field.generalProperties.fieldApiName !== 'medicine_code'),
          // Pass any data you need to the dialog here
        }
      }).afterClosed().subscribe(result => {
        if (result) {
          // Handle the result from the dialog
        }
      });
    }
  }

}